/**
 * Session Context Helper — The Brain of Session Isolation
 * 
 * Rules:
 * 1. Isolation: Guests (Invite links) live in sessionStorage; Clients live in localStorage.
 * 2. Stickiness: Once a tab is identified as a Guest context (via URL token), it REMAINS
 *    a guest context for the life of that tab/session, even if the URL changes.
 * 3. Abstraction: Consumers (hooks, store) should simply ask to "save token" or "get storage",
 *    without checking "isGuestContext" manually everywhere.
 */

import { tokenV3Name, tokenName, userName as userNameKey, activityName } from 'config/constants';

// Key used to mark a tab strictly as a guest context
const GUEST_MARKER_KEY = 'cincel_context_is_guest';

// ─── Context Detection ───────────────────────────────────────────────────────

/**
 * Determines if the current browsing context is a Guest (invitation) context.
 * 
 * Logic:
 * 1. If URL has '?token=', it IS a guest context (and we mark it stickily).
 * 2. If sessionStorage has the sticky marker, it IS a guest context.
 * 
 * This prevents the session from "becoming client" just because the user 
 * navigated to a route without query params.
 */
export function isGuestContext(): boolean {
    if (typeof window === 'undefined') return false;

    // 1. Check URL (Priority detection)
    const params = new URLSearchParams(window.location.search);
    if (params.has('token')) {
        // Mark this tab as guest-tainted permanently
        sessionStorage.setItem(GUEST_MARKER_KEY, 'true');
        return true;
    }

    // 2. Check Sticky Marker
    return sessionStorage.getItem(GUEST_MARKER_KEY) === 'true';
}

/**
 * Returns the raw invitation token (scoped) from the URL, if present.
 */
export function getInviteTokenFromUrl(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);
        return params.get('token');
    } catch {
        return null;
    }
}

// ─── Storage Abstraction ─────────────────────────────────────────────────────

/**
 * Returns the correctly isolated storage engine for the current context.
 * - Guest Context → sessionStorage (Ephemeral, per-tab)
 * - Client Context → localStorage (Persistent)
 */
export function getStorage(): Storage {
    return isGuestContext() ? sessionStorage : localStorage;
}

// ─── Token Management (Tell, Don't Ask) ──────────────────────────────────────

/**
 * Gets the V3 Token.
 * - In Guest Mode: Prioritizes the logged-in session token (in sessionStorage).
 *   Falls back to the URL Invite Token if no login session exists.
 * - In Client Mode: Returns localStorage token.
 */
export function getSessionTokenV3(): string | null {
    const storage = getStorage();
    const storedToken = storage.getItem(tokenV3Name);

    if (storedToken) return storedToken;

    // Special Case: Guest Context fallback to URL token
    if (isGuestContext()) {
        return getInviteTokenFromUrl();
    }

    return null;
}

/**
 * Gets the Legacy V1 Token.
 */
export function getLegacyToken(): string | null {
    return getStorage().getItem(tokenName);
}

/**
 * Saves the V3 Token to the correct isolated storage.
 */
export function setSessionTokenV3(token: string): void {
    getStorage().setItem(tokenV3Name, token);
}

/**
 * Saves the Legacy V1 Token to the correct isolated storage.
 */
export function setLegacyToken(token: string): void {
    getStorage().setItem(tokenName, token);
}

/**
 * Resolves the V3 token for API usage.
 * Prefer explicit token > Stored Session Token > URL Token (via getSessionTokenV3 logic).
 */
export function resolveTokenV3(explicitToken?: string | null): string {
    if (explicitToken) return explicitToken;
    return (getSessionTokenV3() ?? '') as string;
}

// ─── User Data (Tell, Don't Ask) ─────────────────────────────────────────────

export function setSessionUser(user: any): void {
    getStorage().setItem(userNameKey, JSON.stringify(user));
}

export function getSessionUser(): any {
    const data = getStorage().getItem(userNameKey);
    try {
        return data ? JSON.parse(data) : null;
    } catch {
        return null;
    }
}

// ─── Activity / Timeout Isolation ────────────────────────────────────────────

/**
 * Sets the last activity timestamp in the correct storage.
 * This allows guests to have their own idle timeout loop per tab,
 * independent of the client's global timeout.
 */
export function setLastActivity(): void {
    getStorage().setItem(activityName, Date.now().toString());
}

export function getLastActivity(): number {
    const val = getStorage().getItem(activityName);
    return val ? parseInt(val, 10) : 0;
}

/**
 * Returns true if a guest tab has completed OTP login (i.e. has a stored
 * tokenV3 in sessionStorage, rather than just relying on the URL invite token).
 * For non-guest contexts this always returns false.
 */
export function hasGuestOtpSession(): boolean {
    if (!isGuestContext()) return false;
    return !!sessionStorage.getItem(tokenV3Name);
}

/**
 * Checks if ANY session is active.
 * Used for routing guards.
 */
export function hasAnySession(): boolean {
    const storage = getStorage();
    return !!(storage.getItem(tokenV3Name) || storage.getItem(tokenName));
}

// ─── Cleanup ─────────────────────────────────────────────────────────────────

/**
 * Clears the session from the current context.
 * If Guest → Clears sessionStorage (only this tab's session).
 * If Client → Clears localStorage (global sign out).
 */
export function clearSession(): void {
    const storage = getStorage();
    storage.removeItem(tokenName);
    storage.removeItem(tokenV3Name);
    storage.removeItem(userNameKey);
    storage.removeItem(activityName);

    // If we clear a guest session, we also untaint the tab?
    // Debatable. For safety, let's keep the marker so they stay "guest" (logged out)
    // rather than accidentally falling back to client if they reload.
    // But usually clearSession implies "Logout", so maybe we simply clear tokens.
}

/**
 * Signals other tabs to unlock the session (Client context only).
 * Guest sessions are isolated per-tab and should NOT unlock the main client session.
 */
export function setSessionUnlockSignal(): void {
    // Write to the current storage. 
    // If Guest (sessionStorage), this is isolated and won't trigger cross-tab events (desired).
    // If Client (localStorage), this triggers the 'storage' event in other tabs (desired).
    getStorage().setItem('cincel_session_unlocked', `cincel_unlock_${Date.now()}`);
}
